import React, { useCallback, useEffect, useState } from 'react';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import styled from 'styled-components';
import { ErrorState } from '@ardoq/status-ui';
import { FlexBox } from '@ardoq/layout';
import {
  ImagePreviewProps,
  PreviewStateStatus,
  ImagePreviewState,
} from './types';
import { isShowControls } from './utils';
import { ContentControls } from './ContentControls';
import { DEFAULT_CONTENT_CONTROLS, FULL_TRANSPARENT } from './const';
import { ContentDisplay } from './atoms';
import { PreviewContent } from '@ardoq/api-types';
import { LoadingOverlay } from '@ardoq/loader';

const ContentContainer = styled.div<{
  $backgroundColor?: string;
  $height?: string;
}>`
  background-color: ${props => props.$backgroundColor || FULL_TRANSPARENT};
  padding: 0;
  height: ${props => props.$height || '500px'};
  position: relative;
  display: flex;
  justify-content: center;
`;

const ContentPreview = styled.div<{
  $isHidden: boolean;
}>`
  display: ${props => (props.$isHidden ? 'none' : 'block')};
`;

const ZoomableContent = React.memo(
  ({ content, contentType }: PreviewContent) => {
    const { zoomToElement } = useControls();
    const [isCenteredInitially, setIsCenteredInitially] = useState(false);

    const centerContentOnLoad = useCallback(() => {
      zoomToElement('imagePreviewContent', 0, 0);
      setIsCenteredInitially(true);
    }, [zoomToElement]);

    return (
      <TransformComponent wrapperStyle={{ height: '100%', width: '100%' }}>
        <ContentPreview $isHidden={!isCenteredInitially}>
          <ContentDisplay
            src={content}
            centerView={centerContentOnLoad}
            contentType={contentType}
          />
        </ContentPreview>
      </TransformComponent>
    );
  }
);

/**
 * This is dumb component that only signals what should be done, so the
 * application layer is responsible for implementing both fetching and
 * state management.
 * Reducers and types are provided by the package for convenience
 */
export const ImagePreview = ({
  maxScale = 2,
  minScale = 0.5,
  zoomStep = 0.2,
  controls = DEFAULT_CONTENT_CONTROLS,
  link,
  url,
  height,
  backgroundColor,
  zoomInOptions,
  commands: { onChangeBackgroundColor, onFetchImage },
  contentControlsPosition,
  limitToBounds,
  labels,
  ...previewState
}: ImagePreviewProps & ImagePreviewState) => {
  useEffect(() => {
    onFetchImage(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <ContentContainer $backgroundColor={backgroundColor} $height={height}>
      {previewState.status === PreviewStateStatus.Error && (
        <FlexBox align="center" justify="center" width="full">
          <ErrorState
            role="alert"
            layout="vertical"
            title={previewState.errorContent.title}
            error={previewState.errorContent.error}
          />
        </FlexBox>
      )}

      {previewState.status === PreviewStateStatus.Loading && <LoadingOverlay />}

      {previewState.status === PreviewStateStatus.Loaded && (
        <TransformWrapper
          maxScale={maxScale}
          minScale={minScale}
          limitToBounds={limitToBounds}
        >
          {isShowControls(controls) && (
            <ContentControls
              backgroundColor={backgroundColor}
              maxScale={maxScale}
              minScale={minScale}
              zoomStep={zoomStep}
              link={link}
              onChangeBackgroundColor={onChangeBackgroundColor}
              zoomInOptions={zoomInOptions}
              buttonsPosition={contentControlsPosition}
              labels={labels}
            />
          )}

          <ZoomableContent
            contentType={previewState.content.contentType}
            content={previewState.content.content}
          />
        </TransformWrapper>
      )}
    </ContentContainer>
  );
};
